import { ExclamationCircleIcon } from '@heroicons/react/outline'
import clsx from 'clsx'
import { FieldProps, getIn } from 'formik'
import React, { useEffect, useState } from 'react'

const FormikDragZone: React.ComponentType<FieldProps & { label: string; type: string; placeholderImage?: string }> = ({
  field,
  form,
  label,
  placeholderImage,
  ...rest
}) => {
  const [preview, setPreview] = useState<string>()
  const error = getIn(form.errors, field.name)
  const isTouched = getIn(form.touched, field.name)
  const hasError = isTouched && error

  useEffect(() => {
    if (placeholderImage) {
      setPreview(placeholderImage)
    }
  }, [placeholderImage])

  return (
    <div className="relative">
      <label htmlFor="cover-photo" className="mb-1 block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
        {label}
      </label>

      <div className="group flex items-center justify-center">
        <input
          id={field.name}
          className="absolute top-0 left-0 z-20 h-full w-full cursor-pointer opacity-0 "
          {...field}
          {...rest}
          type="file"
          value={undefined}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              setPreview(URL.createObjectURL(e.target.files[0]))
              form.setFieldValue(field.name, e.target.files[0])
            }
          }}
        />
        {(preview || placeholderImage) && (
          <img
            loading="lazy"
            className="h-full max-h-72 w-full object-contain transition-opacity group-hover:opacity-10"
            src={preview}
            alt=""
          />
        )}

        <div
          className={clsx({
            'mt-1 w-full sm:col-span-2 sm:mt-0': true,
            'absolute ml-3 w-full opacity-0 transition-opacity group-hover:opacity-100': preview,
          })}
        >
          <div className="flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer  rounded-md font-medium text-brand-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-500 focus-within:ring-offset-2 hover:text-brand-500"
                >
                  <span>Upload a file</span>
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
            </div>
          </div>
        </div>
      </div>

      {hasError ? (
        <div className="pointer-events-none inset-y-0 mt-2 flex items-center pr-3">
          <ExclamationCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
          <p className="mt-1 text-xs text-red-900" id={`${field.name}-error`}>
            {error}
          </p>
        </div>
      ) : null}
    </div>
  )
}

export default FormikDragZone
